"use client";
import { API_URL } from "@/lib/config";

import { useEffect, useState } from "react";
import { X, Info, AlertTriangle, Wrench, Sparkles } from "lucide-react";

const API = API_URL;
const DISMISSED_KEY = "dismissed_announcements";

interface Announcement {
  id: string;
  title: string;
  body: string;
  type: string;
  url: string | null;
}

const TYPE_STYLES: Record<string, { bar: string; icon: React.ElementType; iconColor: string }> = {
  info:        { bar: "bg-blue-600/90 border-blue-400/30",    icon: Info,          iconColor: "text-blue-200" },
  warning:     { bar: "bg-yellow-600/90 border-yellow-400/30", icon: AlertTriangle, iconColor: "text-yellow-200" },
  maintenance: { bar: "bg-orange-600/90 border-orange-400/30", icon: Wrench,        iconColor: "text-orange-200" },
  feature:     { bar: "bg-purple-600/90 border-purple-400/30", icon: Sparkles,      iconColor: "text-purple-200" },
};

function getDismissed(): string[] {
  if (typeof window === "undefined") return [];
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]"); }
  catch { return []; }
}

function saveDismissed(ids: string[]) {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids)); } catch {}
}

export default function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    setDismissed(getDismissed());
    fetch(`${API}/api/announcements`)
      .then((r) => r.json())
      .then((d) => setAnnouncements(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    saveDismissed(next);
  };

  const visible = announcements.filter((a) => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  // Show only the first non-dismissed announcement
  const a = visible[0];
  const style = TYPE_STYLES[a.type] ?? TYPE_STYLES.info;
  const Icon = style.icon;

  return (
    <div className={`w-full ${style.bar} backdrop-blur-md border-b flex items-center gap-3 px-4 py-2.5 z-[500] relative`}>
      <Icon className={`w-4 h-4 shrink-0 ${style.iconColor}`} />
      <p className="text-sm text-ink flex-1 min-w-0">
        <span className="font-semibold mr-1.5">{a.title}</span>
        <span className="text-ink/80">{a.body}</span>
        {a.url && (
          <a href={a.url} target="_blank" rel="noopener noreferrer"
            className="ml-2 underline underline-offset-2 text-ink/90 hover:text-ink text-xs">
            Learn more
          </a>
        )}
      </p>
      <button
        onClick={() => dismiss(a.id)}
        className="p-1 rounded-md hover:bg-ink/15 transition-colors shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5 text-ink/70" />
      </button>
    </div>
  );
}
